/**
 * @file BlendSpecs.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-14
 * @modified 2026-08-14
 * @description The Specs tab — the nutrient targets a blend can be checked
 *              against.
 *
 *              A spec is what turns the Calculator's analysis panel from a set
 *              of numbers into a pass or a fail: attach one and each targeted
 *              nutrient gets a band bar showing whether the blend meets it. This
 *              tab is where those bands are set.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import Input from './Input';
import Dropdown from './Dropdown';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import AuditHistoryModal from './AuditHistoryModal';
import ConfirmDialog from './ConfirmDialog';
import BlendSpecModal from './BlendSpecModal';
import { useSlideOutButton } from './SlideOutButton';
import { BLEND_TYPE_OPTIONS } from '../utils/blendTypes';
import './SearchLayout.css';
import './TableStyles.css';
import './BlendCalculator.css';

const TYPE_FILTER = [{ value: '', label: 'All types' }, ...BLEND_TYPE_OPTIONS.slice(1)];

/** "17.5 – 18.5", "≥ 12.8", "≤ 9" — whichever bounds are set. */
function bandText(t) {
  const hasMin = t?.min !== undefined && t?.min !== null;
  const hasMax = t?.max !== undefined && t?.max !== null;
  if (hasMin && hasMax) return `${t.min} – ${t.max}`;
  if (hasMin) return `≥ ${t.min}`;
  if (hasMax) return `≤ ${t.max}`;
  return '—';
}

export default function BlendSpecs({ user }) {
  const [search, setSearch] = usePersistedState('blendSpecs.search', '');
  const [type, setType] = usePersistedState('blendSpecs.type', '');

  const [specs, setSpecs] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);     // spec object, or {} for new
  const [historyFor, setHistoryFor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [specRes, compRes] = await Promise.all([
        fetch(`${API_URL}/api/blends/specs?includeInactive=true`, { credentials: 'include' }),
        fetch(`${API_URL}/api/blends/nutrients`, { credentials: 'include' })
      ]);
      if (!specRes.ok) throw new Error((await specRes.json()).error || 'Failed to load specs');
      setSpecs((await specRes.json()).specs || []);
      if (compRes.ok) setComponents((await compRes.json()).components || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (spec) => {
    try {
      const res = await fetch(`${API_URL}/api/blends/specs/${spec.id}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
      setNotice(`${spec.name} deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setError(e.message);
      setConfirmDelete(null);
    }
  }, [load]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return specs.filter((s) => {
      if (type && s.blend_type !== type) return false;
      if (!term) return true;
      return `${s.name} ${s.blend_type || ''} ${s.description || ''}`.toLowerCase().includes(term);
    });
  }, [specs, search, type]);

  const buildRowActions = useCallback((spec) => [
    { key: 'edit', icon: 'edit', label: `Edit ${spec.name}`, onClick: () => setEditing(spec) },
    { key: 'history', icon: 'history', label: 'View history', onClick: () => setHistoryFor(spec) },
    {
      key: 'delete', icon: 'delete', label: `Delete ${spec.name}`,
      className: 'action-danger', onClick: () => setConfirmDelete(spec)
    }
  ], []);

  const ctxMenu = useTableContextMenu({ rows, selectedIds: new Set(), getRowId: (s) => s.id });

  useSlideOutButton([loading, rows.length]);

  const labelFor = useCallback(
    (key) => components.find((c) => c.key === key)?.label || key, [components]);

  return (
    <div className="search-layout blend-calculator blend-specs" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Target Specs</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              The nutrient bands a blend is checked against. Attach one on the
              Calculator and each targeted nutrient shows whether the blend meets it.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => setEditing({})} title="New spec">
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">New spec</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={load} title="Reload">
                <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 175px' }}>
          <div className="filter-group">
            <label>Search</label>
            <Input type="text" icon="search" value={search}
                   placeholder="Spec name, type or description…"
                   onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Type</label>
            <Dropdown options={TYPE_FILTER} value={type} onChange={setType} />
          </div>
        </div>
      </form>

      <div className="search-content">
        {error && (
          <div className="blend-warning inline">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
            <button className="btn-icon" onClick={() => setError(null)}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        )}
        {notice && !error && (
          <div className="blend-notice inline">
            <span className="material-symbols-rounded">check_circle</span>
            <span>{notice}</span>
            <button className="btn-icon" onClick={() => setNotice(null)}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        )}

        {loading && (
          <div className="loading">
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span>Loading specs…</span>
          </div>
        )}

        {!loading && !rows.length && (
          <div className="table-empty">
            <span className="material-symbols-rounded">rule</span>
            <span>
              {specs.length
                ? 'No specs match those filters.'
                : 'No target specs yet. Use New spec to add the first one.'}
            </span>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="table-container">
            <table className="data-table blend-specs-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th>Spec</th>
                  <th>Type</th>
                  <th>Targets</th>
                  <th className="actions-col">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((spec) => {
                  const targets = Object.entries(spec.targets || {});
                  return (
                    <tr key={spec.id}
                        onContextMenu={ctxMenu.onRowContextMenu(spec)}
                        onDoubleClick={() => setEditing(spec)}>
                      <td>
                        <span className="blend-material-name">{spec.name}</span>
                        {spec.description && (
                          <span className="blend-material-meta">{spec.description}</span>
                        )}
                      </td>
                      <td>{spec.blend_type || '—'}</td>
                      <td>
                        {targets.length ? (
                          <div className="blend-spec-chips">
                            {targets.map(([key, t]) => (
                              <span key={key} className="blend-spec-chip">
                                {labelFor(key)} <strong>{bandText(t)}</strong>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="blend-spec-none">
                            Nothing targeted — this spec passes everything
                          </span>
                        )}
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu actions={buildRowActions(spec)} triggerTitle="Spec actions" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'new', icon: 'add', label: 'New spec', onClick: () => setEditing({}) },
          { key: 'refresh', icon: 'refresh', label: 'Reload', onClick: load }
        ]}
      />

      {editing && (
        <BlendSpecModal
          spec={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(saved, what) => {
            setEditing(null);
            setNotice(`${saved?.name || 'Spec'} ${what}`);
            load();
          }}
        />
      )}

      {historyFor && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryFor(null)}
          entityType="blend_spec"
          entityId={historyFor.id}
          entityIdentifier={historyFor.name}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          isOpen
          title="Delete this spec?"
          message={`"${confirmDelete.name}" will be removed. Blends currently using it keep their figures but lose the pass/fail check.`}
          confirmText="Delete"
          onConfirm={() => remove(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
